import { useState, type ReactNode } from 'react';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@mui/material';
import { isSupportedLocale, supportedLocales, type SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { AccountActions, AccountState } from './account';
import { logoAddress } from './logo';
import { isFamilyView, isNavigationActive, navigationGroups, navigationLabel } from './navigation';
import { NavigationIcon } from './NavigationIcon';
import type { NavigationItem } from './routes';
import { appTheme } from './theme';

export function AppFrame({
  locale,
  onLocaleChange,
  account,
  actions,
  navigation,
  children,
}: {
  locale: SupportedLocale;
  onLocaleChange: (locale: SupportedLocale) => void;
  account: AccountState;
  actions: AccountActions;
  navigation: readonly NavigationItem[];
  children: ReactNode;
}): React.JSX.Element {
  const [mobileMenuPath, setMobileMenuPath] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const menuOpen = mobileMenuPath === location.pathname;
  const signedIn = account.status === 'signedIn' ? account : null;
  const organizationId = signedIn?.me.activeOrganizationId ?? undefined;
  const membership = signedIn?.me.memberships.find(
    (item) => item.organizationId === organizationId,
  );
  const familyView = isFamilyView(signedIn?.me.permissions ?? []);
  const hasNavigation = navigation.length > 0;

  function navigationLink(item: NavigationItem): React.JSX.Element {
    const active = isNavigationActive(item, location.pathname);
    return (
      <Button
        key={item.path}
        component={Link}
        to={item.path}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          setMobileMenuPath(null);
        }}
        startIcon={<NavigationIcon permission={item.permission} />}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          py: 1,
          px: 1.5,
          my: 0.25,
          color: active ? 'primary.main' : 'text.secondary',
          bgcolor: active ? '#e8f3ef' : 'transparent',
          '&:hover': { bgcolor: active ? '#dcece5' : '#f1f4f6' },
        }}
      >
        {translate(locale, navigationLabel(item, familyView))}
      </Button>
    );
  }

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Button
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: -10_000,
          '&:focus': { left: 8, top: 8, zIndex: 2000, bgcolor: 'background.paper' },
        }}
      >
        {translate(locale, 'shell.skipToContent')}
      </Button>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5, px: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
            {signedIn?.me.logoVersion != null && organizationId !== undefined ? (
              <Box
                component="img"
                src={logoAddress(organizationId, signedIn.me.logoVersion)}
                alt={membership?.organizationName ?? ''}
                sx={{ height: 42, maxWidth: 100, objectFit: 'contain' }}
              />
            ) : (
              <Box
                aria-hidden="true"
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <NavigationIcon permission="teams.view" />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                {translate(locale, 'app.name')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {translate(locale, 'app.tagline')}
              </Typography>
            </Box>
          </Box>
          {hasNavigation && (
            <Button
              variant="outlined"
              aria-controls="main-navigation"
              aria-expanded={menuOpen}
              onClick={() => {
                setMobileMenuPath(menuOpen ? null : location.pathname);
              }}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, minWidth: 0, px: 1.5 }}
            >
              {translate(locale, 'navigation.menu')}
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {signedIn !== null && signedIn.me.memberships.length > 1 && (
              <FormControl
                size="small"
                sx={{
                  flex: { xs: '1 1 150px', sm: '0 1 200px' },
                  minWidth: 140,
                  maxWidth: { sm: 220 },
                }}
              >
                <InputLabel id="organization-label">
                  {translate(locale, 'account.organization')}
                </InputLabel>
                <Select
                  labelId="organization-label"
                  value={organizationId ?? ''}
                  label={translate(locale, 'account.organization')}
                  onChange={(event) => {
                    setMobileMenuPath(null);
                    void actions
                      .selectOrganization(event.target.value)
                      .then(() => navigate('/', { replace: true }));
                  }}
                >
                  {signedIn.me.memberships.map((item) => (
                    <MenuItem key={item.organizationId} value={item.organizationId}>
                      {item.organizationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl
              size="small"
              sx={{ minWidth: 125, flex: { xs: '1 1 125px', sm: '0 0 140px' } }}
            >
              <InputLabel id="locale-label">{translate(locale, 'locale.label')}</InputLabel>
              <Select
                labelId="locale-label"
                value={locale}
                label={translate(locale, 'locale.label')}
                onChange={(event) => {
                  if (isSupportedLocale(event.target.value)) {
                    onLocaleChange(event.target.value);
                    actions.rememberLocale(event.target.value);
                  }
                }}
              >
                {supportedLocales.map((value) => (
                  <MenuItem key={value} value={value}>
                    {translate(locale, `locale.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {signedIn !== null && (
              <Button
                color="inherit"
                onClick={() => {
                  void actions.signOut();
                }}
                sx={{ color: 'text.secondary' }}
              >
                {translate(locale, 'account.signOut')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: { md: 'flex' }, minHeight: 'calc(100dvh - 85px)' }}>
        {hasNavigation && (
          <Box
            component="aside"
            sx={{
              display: { xs: menuOpen ? 'block' : 'none', md: 'block' },
              width: { md: 250 },
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderRight: { md: '1px solid #e2e8ed' },
              borderBottom: { xs: '1px solid #e2e8ed', md: 0 },
              borderColor: 'divider',
            }}
          >
            <Box
              component="nav"
              id="main-navigation"
              aria-label={translate(locale, 'navigation.title')}
              sx={{
                padding: 2,
                position: { md: 'sticky' },
                top: { md: 110 },
                maxHeight: { md: 'calc(100dvh - 110px)' },
                overflowY: { md: 'auto' },
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', px: 1.5, mb: 0.5 }}
              >
                {translate(locale, 'navigation.workspace')}
              </Typography>
              {membership !== undefined && (
                <Typography
                  variant="body2"
                  sx={{ px: 1.5, mb: 2, fontWeight: 600, overflowWrap: 'anywhere' }}
                >
                  {membership.organizationName}
                </Typography>
              )}
              {navigation.filter((item) => item.permission === 'overview.view').map(navigationLink)}
              {navigationGroups.map((group) => {
                const items = navigation.filter((item) =>
                  group.permissions.includes(item.permission),
                );
                if (items.length === 0) return null;
                return (
                  <Box key={group.titleKey} sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', px: 1.5, mb: 0.5, fontWeight: 600 }}
                    >
                      {translate(locale, group.titleKey)}
                    </Typography>
                    {items.map(navigationLink)}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            maxWidth: 1440,
            mx: 'auto',
            padding: { xs: 2, sm: 3, lg: 4 },
            '& > section': {
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              padding: { xs: 2, sm: 3 },
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
